/**
 * Animation helpers exposed to Blazor via JSInterop, e.g.
 * await JSRuntime.InvokeVoidAsync("animationUtils.fadeIn", "elementId", 300);
 */

export const animationUtils = {
  fadeIn(elementId: string, duration: number = 300): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      element.style.opacity = '0';
      element.style.transition = `opacity ${duration}ms ease-in-out`;

      // Trigger reflow
      element.offsetHeight;

      element.style.opacity = '1';

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  },

  fadeOut(elementId: string, duration: number = 300): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      element.style.transition = `opacity ${duration}ms ease-in-out`;
      element.style.opacity = '0';

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  },

  slideIn(
    elementId: string,
    direction: 'left' | 'right' | 'top' | 'bottom' = 'left',
    duration: number = 300
  ): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const transforms = {
        left: 'translateX(-100%)',
        right: 'translateX(100%)',
        top: 'translateY(-100%)',
        bottom: 'translateY(100%)'
      };

      element.style.transform = transforms[direction];
      element.style.transition = `transform ${duration}ms ease-in-out`;

      // Trigger reflow
      element.offsetHeight;

      element.style.transform = 'translate(0, 0)';

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  },

  slideOut(
    elementId: string,
    direction: 'left' | 'right' | 'top' | 'bottom' = 'left',
    duration: number = 300
  ): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const transforms = {
        left: 'translateX(-100%)',
        right: 'translateX(100%)',
        top: 'translateY(-100%)',
        bottom: 'translateY(100%)'
      };

      element.style.transition = `transform ${duration}ms ease-in-out`;
      element.style.transform = transforms[direction];

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  },

  scale(elementId: string, scale: number, duration: number = 300): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      element.style.transition = `transform ${duration}ms ease-in-out`;
      element.style.transform = `scale(${scale})`;

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  },

  rotate(elementId: string, degrees: number, duration: number = 300): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      element.style.transition = `transform ${duration}ms ease-in-out`;
      element.style.transform = `rotate(${degrees}deg)`;

      setTimeout(() => {
        element.style.transition = '';
        resolve();
      }, duration);
    });
  },

  shake(elementId: string, intensity: number = 10): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const originalTransform = element.style.transform;
      const animation = [
        { transform: `translateX(0)` },
        { transform: `translateX(${intensity}px)` },
        { transform: `translateX(-${intensity}px)` },
        { transform: `translateX(${intensity}px)` },
        { transform: `translateX(-${intensity}px)` },
        { transform: `translateX(0)` }
      ];

      element.animate(animation, {
        duration: 400,
        easing: 'ease-in-out'
      }).onfinish = () => {
        element.style.transform = originalTransform;
        resolve();
      };
    });
  },

  pulse(elementId: string, scale: number = 1.1, duration: number = 600): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const animation = [
        { transform: 'scale(1)' },
        { transform: `scale(${scale})` },
        { transform: 'scale(1)' }
      ];

      element.animate(animation, {
        duration,
        easing: 'ease-in-out'
      }).onfinish = () => {
        resolve();
      };
    });
  },

  flash(elementId: string, color: string = '#ffeb3b', duration: number = 300): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const originalBackground = element.style.backgroundColor;

      element.style.transition = `background-color ${duration}ms ease-in-out`;
      element.style.backgroundColor = color;

      setTimeout(() => {
        element.style.backgroundColor = originalBackground;
        setTimeout(() => {
          element.style.transition = '';
          resolve();
        }, duration);
      }, duration);
    });
  },

  bounce(elementId: string): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const animation = [
        { transform: 'translateY(0)' },
        { transform: 'translateY(-30px)', offset: 0.4 },
        { transform: 'translateY(0)', offset: 0.6 },
        { transform: 'translateY(-15px)', offset: 0.8 },
        { transform: 'translateY(0)' }
      ];

      element.animate(animation, {
        duration: 800,
        easing: 'ease-out'
      }).onfinish = () => {
        resolve();
      };
    });
  },

  countUp(elementId: string, targetValue: number, duration: number = 2000): Promise<void> {
    return new Promise((resolve) => {
      const element = document.getElementById(elementId);
      if (!element) {
        console.warn(`Element with ID "${elementId}" not found`);
        resolve();
        return;
      }

      const startValue = parseFloat(element.textContent || '0');
      const startTime = Date.now();
      const endTime = startTime + duration;

      const updateNumber = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / duration, 1);
        const currentValue = startValue + (targetValue - startValue) * progress;

        element.textContent = Math.floor(currentValue).toString();

        if (now < endTime) {
          requestAnimationFrame(updateNumber);
        } else {
          element.textContent = targetValue.toString();
          resolve();
        }
      };

      requestAnimationFrame(updateNumber);
    });
  }
};

if (typeof window !== 'undefined') {
  (window as any).animationUtils = animationUtils;
}
